"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { CarIcon, ChevronDown, ContactIcon, Menu, SettingsIcon, X } from "lucide-react";

import {
   Sidebar,
   SidebarContent,
   SidebarFooter,
   SidebarHeader,
   SidebarMenu,
   SidebarMenuButton,
   SidebarMenuItem,
   SidebarGroup,
   SidebarGroupContent,
   SidebarGroupLabel,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { LogoutLink } from "@kinde-oss/kinde-auth-nextjs";
import { DashboardIcon, MyProductsIcon, InsuranceIcon, MaintenanceIcon, RepairsIcon, ServiceIcon, HelpIcon, ServicePackagesIcon, ThemeIcon, CompanyIcon, SupportIcon } from "@/components/icons";
import { useTheme } from "@/components/provider/ThemeProvider";

const mainNavItems = [
   { title: "Dashboard", href: "/", icon: DashboardIcon },
   { title: "Mijn fietsen", href: "/mijn-fietsen", icon: MyProductsIcon },
   {
      title: "Fietsverzekeringen",
      href: "/fietsverzekeringen",
      icon: InsuranceIcon,
   },
   {
      title: "Onderhoudsoverzicht",
      href: "/onderhoudsoverzicht",
      icon: MaintenanceIcon,
   },
   {
      title: "Reparatie inplannen",
      href: "/reparatie-inplannen",
      icon: RepairsIcon,
   },
   {
      title: "Servicebeurt inplannen",
      href: "/servicebeurt-inplannen",
      icon: ServiceIcon,
   },
   { title: "Contactgegevens", href: "/contact", icon: ContactIcon },
   {
      title: "Help",
      href: "https://help.towobike.nl/",
      icon: HelpIcon,
      target: "_blank",
   },
];

const adminNavItems = [
   { title: "Servicepakketten", href: "/servicepakketten", icon: ServicePackagesIcon },
   { title: "Huisstijl", href: "/huisstijl", icon: ThemeIcon },
   { title: "Filialen", href: "/filialen", icon: CompanyIcon },
   {
      title: "Support",
      href: "https://help.towobike.nl/",
      target: "_blank",
      icon: SupportIcon,
   },
   { title: "Roadmap", href: "/roadmap", icon: CarIcon },
   { title: "Instellingen", href: "/instellingen", icon: SettingsIcon },
];

interface MainNavProps {
   isAdmin: boolean;
   name: string;
}

export function MainNav(props: MainNavProps) {
   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
   const { theme } = useTheme();

   useEffect(() => {
      // Prevent scrolling on the body when the mobile menu is open
      if (mobileMenuOpen) {
         document.body.classList.add("overflow-hidden");
      } else {
         document.body.classList.remove("overflow-hidden");
      }

      // Cleanup function to remove the class when the component unmounts
      return () => {
         document.body.classList.remove("overflow-hidden");
      };
   }, [mobileMenuOpen]);

   return (
      <>
         {/* Mobile Navigation Header */}
         <div className="lg:hidden flex items-center justify-between p-4 border-b bg-white">
            <Link href="/" className="flex items-center space-x-2">
               <img
                  src={theme?.logoUrl ?? "/logo.png"}
                  className=""
                  alt="logo"
                  width={40}
                  height={40}
                  draggable="false"
               />
            </Link>
            <Button
               variant="ghost"
               className="p-2 focus:bg-white"
               onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
               aria-label="Toggle menu"
            >
               {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
               ) : (
                  <Menu className="h-6 w-6" />
               )}
            </Button>
         </div>

         {/* Mobile Sidebar */}
         <div
            className={`mt-16 pointer-events-none bg-white fixed top-0 right-0 w-full h-full z-40 overflow-y-scroll transition-transform duration-300 ease-in-out transform ${mobileMenuOpen ? "translate-x-0" : "translate-x-full"
               } lg:hidden`}
            style={{ maxWidth: "100vw" }}
         >
            <div className="p-4 pointer-events-auto pb-16">
               <NavContent
                  isAdmin={props.isAdmin}
                  onClick={() => setMobileMenuOpen(false)}
               />
               <SidebarFooter className="border-t p-4">
                  <DropdownMenu>
                     <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="w-full justify-start">
                           <span className="flex items-center">
                              {props.name}
                              <ChevronDown className="ml-auto h-4 w-4" />
                           </span>
                        </Button>
                     </DropdownMenuTrigger>
                     <DropdownMenuContent
                        align="start"
                        alignOffset={11}
                        className="w-[200px]"
                        forceMount
                     >
                        <DropdownMenuItem>
                           <LogoutLink
                              postLogoutRedirectURL={`https://${process.env.NEXT_PUBLIC_BASE_DOMAIN}`}
                              className={`flex items-center p-2 hover:scale-105 transition-all duration-200`}
                           >
                              Uitloggen
                           </LogoutLink>
                        </DropdownMenuItem>
                     </DropdownMenuContent>
                  </DropdownMenu>
               </SidebarFooter>
            </div>
         </div>


         {/* Desktop Sidebar */}
         <Sidebar className="hidden lg:flex border-r">
            <SidebarHeader className="border-b p-6">
               <Link href="/" className="flex items-center justify-center space-x-2">
                  <img
                     src={theme?.logoUrl ?? "/logo.png"}
                     className=""
                     alt="logo"
                     width={120}
                     height={120}
                     draggable="false"
                  />
               </Link>
            </SidebarHeader>
            <NavContent isAdmin={props.isAdmin} />
            <SidebarFooter className="border-t p-4">
               <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                     <Button variant="ghost" className="w-full justify-start">
                        <span className="flex items-center">
                           {props.name}
                           <ChevronDown className="ml-auto h-4 w-4" />
                        </span>
                     </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                     align="start"
                     alignOffset={11}
                     className="w-[200px]"
                     forceMount
                  >
                     <DropdownMenuItem>
                        <LogoutLink
                           postLogoutRedirectURL={`https://${process.env.NEXT_PUBLIC_BASE_DOMAIN}`}
                           className={`flex items-center p-2 hover:scale-105 transition-all duration-200`}
                        >
                           Uitloggen
                        </LogoutLink>
                     </DropdownMenuItem>
                  </DropdownMenuContent>
               </DropdownMenu>
            </SidebarFooter>
         </Sidebar>
      </>
   );
}

function NavContent({
   onClick,
   isAdmin,
}: {
   onClick?: () => void;
   isAdmin: boolean;
}) {
   const pathname = usePathname();

   return (
      <SidebarContent className="mt-4">
         <SidebarGroup>
            <SidebarGroupContent>
               <SidebarMenu>
                  {mainNavItems.map((item, index: number) => (
                     <div key={item.href}>
                        <SidebarMenuItem>
                           <SidebarMenuButton asChild isActive={pathname === item.href}>
                              <Link
                                 href={item.href}
                                 target={item?.target || "_self"}
                                 onClick={onClick}
                              >
                                 <item.icon className="mr-2 h-4 w-4" />
                                 {item.title}
                              </Link>
                           </SidebarMenuButton>
                        </SidebarMenuItem>

                        {index === 0 && <Separator className="my-4" />}
                        {index === 3 && <Separator className="my-4" />}
                        {index === 6 && <Separator className="my-4" />}
                     </div>
                  ))}
               </SidebarMenu>
            </SidebarGroupContent>
         </SidebarGroup>

         <Separator className="mt-2" />

         {isAdmin && (
            <SidebarGroup>
               <SidebarGroupLabel>Admin</SidebarGroupLabel>
               <SidebarGroupContent>
                  <SidebarMenu>
                     {adminNavItems.map((item) => (
                        <SidebarMenuItem key={item.href}>
                           <SidebarMenuButton asChild isActive={pathname === item.href}>
                              <Link
                                 href={item.href}
                                 target={item?.target || "_self"}
                                 onClick={onClick}
                              >
                                 <item.icon className="mr-2 h-4 w-4" />
                                 {item.title}
                              </Link>
                           </SidebarMenuButton>
                        </SidebarMenuItem>
                     ))}
                  </SidebarMenu>
               </SidebarGroupContent>
            </SidebarGroup>
         )}
      </SidebarContent>
   );
}
